import {useCallback} from 'react';
import {useSearchParams} from 'react-router-dom';
import {CampaignStatsCreator} from '@/types/campaignStatsCreator';
import {normalizeCreatorsSort} from '@/views/campaigns/stats/components/creators-stats-table/constants';
import useCampaignStatsCreatorsQuery from '@/hooks/queries/use-campaign-stats-creators-query';

const SIDEBAR_COUNT_PAGE_SIZE = 1;

interface UseCampaignStatsCreatorsControllerParams {
  campaignId: string;
  hasClickTracking?: boolean;
  enabled?: boolean;
}

interface UseCampaignStatsCreatorsControllerResult {
  creators: CampaignStatsCreator[];
  searchQuery: string;
  sort: string;
  isLoading: boolean;
  isFetching: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  error: unknown;
  totalCount: number;
  refetch: () => void;
  handleSearch: (query: string) => void;
  handleSort: (newSort: string) => void;
  handleLoadMore: () => void;
}

function useCampaignStatsCreatorsController({
  campaignId,
  hasClickTracking = false,
  enabled = true,
}: UseCampaignStatsCreatorsControllerParams): UseCampaignStatsCreatorsControllerResult {
  const [searchParams, setSearchParams] = useSearchParams();
  const searchQuery = searchParams.get('q') || '';

  const sort = normalizeCreatorsSort(
    searchParams.get('sort'),
    hasClickTracking
  );

  const {totalCount: sidebarCount} = useCampaignStatsCreatorsQuery({
    campaignId,
    perPage: SIDEBAR_COUNT_PAGE_SIZE,
    enabled: !!campaignId && !enabled,
  });

  const {
    creators,
    isLoading,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch,
    error,
    totalCount: tableCount,
  } = useCampaignStatsCreatorsQuery({
    campaignId,
    sort,
    q: searchQuery,
    enabled,
  });

  const totalCount = enabled ? tableCount : sidebarCount;

  const handleSearch = useCallback(
    (query: string) => {
      const nextSearchParams = new URLSearchParams(searchParams);
      if (query) {
        nextSearchParams.set('q', query);
      } else {
        nextSearchParams.delete('q');
      }
      setSearchParams(nextSearchParams);
    },
    [searchParams, setSearchParams]
  );

  const handleSort = useCallback(
    (newSort: string) => {
      const nextSearchParams = new URLSearchParams(searchParams);
      nextSearchParams.set('sort', newSort);
      setSearchParams(nextSearchParams);
    },
    [searchParams, setSearchParams]
  );

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  return {
    creators,
    searchQuery,
    sort,
    isLoading,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    error,
    totalCount,
    refetch,
    handleSearch,
    handleSort,
    handleLoadMore,
  };
}

export default useCampaignStatsCreatorsController;
export type {UseCampaignStatsCreatorsControllerResult};
